import React, { useState, useEffect } from 'react';
import {
  BarChart3, Brain, Database, Lock, Zap, TrendingUp,
  Users, Globe, ChevronRight, Check, Menu, X,
  Lightbulb, GitBranch, AlertCircle
} from 'lucide-react';

/* ──────────────────────────────────────────────
   GLOBAL STYLES  (injected once via <style> tag)
   ────────────────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=Syne:wght@400;500;600;700;800&display=swap');

  .aion-root { font-family: 'DM Sans', sans-serif; }
  .aion-display { font-family: 'Syne', sans-serif; }

  /* ambient orbs */
  .aion-orb-a {
    position:fixed; top:-30%; left:-20%; width:80%; height:80%;
    background:radial-gradient(ellipse, rgba(79,195,247,.07) 0%, transparent 70%);
    filter:blur(60px); pointer-events:none; z-index:0;
    animation: aion-driftA 18s ease-in-out infinite alternate;
  }
  .aion-orb-b {
    position:fixed; bottom:-20%; right:-15%; width:70%; height:70%;
    background:radial-gradient(ellipse, rgba(60,120,200,.06) 0%, transparent 70%);
    filter:blur(80px); pointer-events:none; z-index:0;
    animation: aion-driftB 22s ease-in-out infinite alternate;
  }
  @keyframes aion-driftA { to { transform:translate(8%,12%) } }
  @keyframes aion-driftB { to { transform:translate(-10%,-8%) } }

  /* noise overlay */
  .aion-noise {
    position:fixed; inset:0; z-index:1; pointer-events:none; opacity:.03;
    background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* hero grid lines */
  .aion-hero-grid {
    position:absolute; inset:0; pointer-events:none;
    background-image:
      linear-gradient(rgba(79,195,247,.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(79,195,247,.04) 1px, transparent 1px);
    background-size:80px 80px;
    -webkit-mask-image:radial-gradient(ellipse 70% 60% at center, black 20%, transparent 75%);
    mask-image:radial-gradient(ellipse 70% 60% at center, black 20%, transparent 75%);
  }

  /* badge pulse dot */
  @keyframes aion-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .aion-pulse { animation: aion-pulse 2s infinite; }

  /* staggered fade-up */
  @keyframes aion-fadeUp {
    from { opacity:0; transform:translateY(20px) }
    to   { opacity:1; transform:translateY(0) }
  }
  .aion-fade-up { animation: aion-fadeUp .85s cubic-bezier(.22,1,.36,1) both; }
  .aion-delay-1 { animation-delay:.12s }
  .aion-delay-2 { animation-delay:.26s }
  .aion-delay-3 { animation-delay:.40s }
  .aion-delay-4 { animation-delay:.56s }

  /* bar chart grow */
  @keyframes aion-growBar {
    from { height:0 !important; opacity:0 }
    to   { opacity:1 }
  }
  .aion-bar { animation: aion-growBar 1s cubic-bezier(.22,1,.36,1) both; }
  .aion-bar-d0 { animation-delay:.15s }
  .aion-bar-d1 { animation-delay:.25s }
  .aion-bar-d2 { animation-delay:.35s }
  .aion-bar-d3 { animation-delay:.45s }
  .aion-bar-d4 { animation-delay:.55s }
  .aion-bar-d5 { animation-delay:.65s }
  .aion-bar-d6 { animation-delay:.75s }

  /* floating card bob */
  @keyframes aion-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
  .aion-float   { animation: aion-float 5s ease-in-out infinite; }
  .aion-float-1 { animation-delay:1.4s; }

  /* card hover lift */
  .aion-card-hover {
    transition: border-color .3s, box-shadow .3s, transform .25s;
  }
  .aion-card-hover:hover {
    border-color: rgba(79,195,247,.28) !important;
    box-shadow: 0 4px 32px rgba(79,195,247,.1);
    transform: translateY(-3px);
  }

  /* features strip dividers */
  .aion-feat-strip > .aion-feat { border-right:1px solid rgba(100,180,255,.12); }
  .aion-feat-strip > .aion-feat:last-child { border-right:none; }

  /* nav shrink on scroll */
  .aion-nav-shrunk {
    padding-top:12px !important;
    padding-bottom:12px !important;
    background:rgba(8,12,20,.85) !important;
  }
`;

function GlobalStyles() {
  return <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />;
}

/* ──────────────────────────────────────────────
   COLOUR / STYLE TOKENS  (shared constants)
   ────────────────────────────────────────────── */
const C = {
  bgDeep:   '#080c14',
  bgCard:   '#0e1420',
  bgGlass:  'rgba(18,24,38,.7)',
  border:   'rgba(100,180,255,.12)',
  borderHi:'rgba(100,180,255,.28)',
  cyan:     '#4fc3f7',
  cyanDim:  '#2a8ab5',
  cyanGlow: 'rgba(79,195,247,.18)',
  white:    '#f0f4f8',
  whiteDim: 'rgba(240,244,248,.55)',
  whiteLo:  'rgba(240,244,248,.32)',
  accent:   '#7dd3fc',
};

const gradCyan  = `linear-gradient(135deg,${C.cyan},${C.cyanDim})`;
const gradText  = { background:`linear-gradient(135deg,${C.cyan},${C.accent})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' };

/* ──────────────────────────────────────────────
   SMALL RE-USABLE PIECES
   ────────────────────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="aion-display flex items-center gap-2.5 text-xs uppercase tracking-widest mb-5"
         style={{ color: C.cyan }}>
      <span style={{ width:28, height:1, background:C.cyan, display:'inline-block' }} />
      {children}
    </div>
  );
}

function SectionTitle({ children, centered }: { children: React.ReactNode; centered?: boolean }) {
  return (
    <h2 className={`aion-display font-bold text-3xl md:text-4xl leading-tight tracking-tight ${centered?'text-center':''}`}
        style={{ color:C.white, letterSpacing:'-0.03em', maxWidth: centered?'100%':520 }}>
      {children}
    </h2>
  );
}

function BtnPrimary({ children, onClick, href }: { children: React.ReactNode; onClick?: () => void; href?: string }) {
  const style: React.CSSProperties = {
    background: gradCyan,
    color:'#0a1018',
    fontWeight:600,
    fontSize:'.95rem',
    padding:'13px 30px',
    borderRadius:10,
    border:'none',
    cursor:'pointer',
    position:'relative',
    overflow:'hidden',
    transition:'box-shadow .3s, transform .15s',
  };
  const Tag = href ? 'a' : 'button';
  return (
    <Tag style={style} onClick={onClick} href={href}
         onMouseEnter={e=>{ e.currentTarget.style.boxShadow=`0 0 26px ${C.cyanGlow}`; e.currentTarget.style.transform='translateY(-1px)'; }}
         onMouseLeave={e=>{ e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}
         className="aion-root">
      {children}
    </Tag>
  );
}

function BtnSecondary({ children, onClick, href }: { children: React.ReactNode; onClick?: () => void; href?: string }) {
  const style: React.CSSProperties = {
    background:'transparent',
    border:`1px solid ${C.borderHi}`,
    color:C.white,
    fontSize:'.95rem',
    fontWeight:500,
    padding:'13px 26px',
    borderRadius:10,
    cursor:'pointer',
    transition:'border-color .25s, background .25s',
  };
  const Tag = href ? 'a' : 'button';
  return (
    <Tag style={style} onClick={onClick} href={href}
         onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.cyanDim; e.currentTarget.style.background='rgba(79,195,247,.06)'; }}
         onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.borderHi; e.currentTarget.style.background='transparent'; }}
         className="aion-root">
      {children}
    </Tag>
  );
}

/* ──────────────────────────────────────────────
   LOGO MARK
   ────────────────────────────────────────────── */
function LogoMark({ size = 32}) {
  return (
    <img 
      src="Logo.png" 
      alt="AION BI Logo" 
      style={{ height: size, width: 'auto' }}
    />
  );
}

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
interface LandingPageProps {
  onLogin: () => void;
}

export default function LandingPage({ onLogin }: LandingPageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled]             = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── data ── */
  const features = [
    { icon:<Brain className="w-5 h-5"/>,      title:'Modelado Automático Inteligente', description:'Detecta automáticamente claves primarias, foráneas, cardinalidades y jerarquías. Genera estructura DER con semántica de negocio.' },
    { icon:<Lightbulb className="w-5 h-5"/>,  title:'Insight Engine Incluido',         description:'Explicaciones automáticas de tendencias, predicciones basadas en ML y alertas inteligentes que anticipan problemas.' },
    { icon:<Database className="w-5 h-5"/>,   title:'Conectividad Universal',         description:'BigQuery, Snowflake, Databricks, Amazon Athena, Google Cloud, AlloyDB. Compatible con cualquier industria.' },
    { icon:<Zap className="w-5 h-5"/>,        title:'Dashboards en Minutos',          description:'Sin necesidad de analistas técnicos ni configuraciones complejas. De datos a insights en minutos, no semanas.' },
    { icon:<GitBranch className="w-5 h-5"/>,  title:'Reconocimiento Inteligente',     description:'Identifica automáticamente dimensiones temporales, tablas puente y relaciones complejas en tus datos.' },
  ];

  const useCases = [
    { title:'Gestión Empresarial',    description:'Integra datos de Finnapp Office: gastos, rendiciones, oportunidades comerciales y pipeline.',      metrics:['Gastos por categoría','Efectividad comercial','Presupuesto vs Real'] },
    { title:'Stakeholders del Agro', description:'Insights de Finnapp Agro Simple: comportamiento de productores, precios regionales y campañas.',  metrics:['Inteligencia territorial','Tendencias de producción','Análisis de riesgo'] },
  ];

  const benefits = [
    { metric:'75%',      label:'Reducción del tiempo de análisis', description:'De semanas a minutos' },
    { metric:'100%',     label:'Automático',                       description:'Sin analistas técnicos' },
    { metric:'Multi',    label:'Fuentes de datos',                 description:'Todo en un solo lugar' },
    { metric:'Real-time',label:'Actualización',                    description:'Datos siempre actuales' },
  ];

  const integrations = ['BigQuery','Snowflake','Databricks','Amazon Athena','Google Cloud','AlloyDB'];

  /* ── render ── */
  return (
    <div className="aion-root" style={{ background:C.bgDeep, color:C.white, minHeight:'100vh', overflowX:'hidden', WebkitFontSmoothing:'antialiased' }}>
      <GlobalStyles />

      <div className="aion-orb-a" />
      <div className="aion-orb-b" />
      <div className="aion-noise" />

      <div style={{ position:'relative', zIndex:2 }}>

        <nav className={`aion-nav-shrunk fixed top-0 left-0 right-0 flex items-center justify-between transition-all duration-300 ${scrolled?'aion-nav-shrunk':''}`}
             style={{ zIndex:100, padding:'20px 48px', background:scrolled?'rgba(8,12,20,.85)':'rgba(8,12,20,.6)', backdropFilter:'blur(18px)', borderBottom:`1px solid ${C.border}` }}>

          <div className="flex items-center">
            <LogoMark size={80} />
          </div>

          <div className="hidden md:flex items-center gap-8">
            {['Características','Cómo Funciona','Casos de Uso','Contacto'].map(l=>(
              <a key={l} href="#" className="transition" style={{ fontSize:'.88rem', color:C.whiteDim }}
                 onMouseEnter={e=>e.currentTarget.style.color=C.white}
                 onMouseLeave={e=>e.currentTarget.style.color=C.whiteDim}>{l}</a>
            ))}
            <BtnPrimary onClick={onLogin}>Iniciar Sesión</BtnPrimary>
          </div>

          <button className="md:hidden" style={{ color:C.white }} onClick={()=>setMobileMenuOpen(o=>!o)}>
            {mobileMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
          </button>
        </nav>

        {mobileMenuOpen && (
          <div className="md:hidden fixed top-16 left-0 right-0 flex flex-col gap-3 px-5 py-5"
               style={{ zIndex:99, background:C.bgCard, borderBottom:`1px solid ${C.border}` }}>
            {['Características','Cómo Funciona','Casos de Uso','Contacto'].map(l=>(
              <a key={l} href="#" style={{ color:C.whiteDim, fontSize:'.9rem' }}>{l}</a>
            ))}
            <BtnPrimary onClick={onLogin}>Iniciar Sesión</BtnPrimary>
          </div>
        )}

        <section className="relative flex flex-col items-center justify-center text-center"
                 style={{ minHeight:'100vh', padding:'140px 5% 100px' }}>
          <div className="aion-hero-grid" />

          <div className="aion-fade-up flex items-center gap-2.5 mb-9"
               style={{ background:C.bgGlass, border:`1px solid ${C.borderHi}`, borderRadius:50, padding:'7px 18px', fontSize:'.82rem', color:C.cyan, letterSpacing:'.04em' }}>
            <span className="aion-pulse" style={{ width:7, height:7, borderRadius:'50%', background:C.cyan, boxShadow:`0 0 8px ${C.cyan}`, display:'inline-block' }} />
            Nuevo · Análisis de datos con IA
          </div>

          <h1 className="aion-display aion-fade-up aion-delay-1 font-bold leading-none"
              style={{ fontSize:'clamp(3rem,7vw,5.2rem)', letterSpacing:'-0.035em', color:C.white, maxWidth:780 }}>
            Se modela, se entiende<br/>
            <span style={gradText}>y se explica solo</span>
          </h1>

          <p className="aion-fade-up aion-delay-2" style={{ marginTop:28, fontSize:'1.05rem', color:C.whiteDim, maxWidth:580, lineHeight:1.65, fontWeight:300 }}>
            La plataforma de Business Intelligence que modela automáticamente tus fuentes de datos y genera insights accionables. Sin necesidad de analistas técnicos.
          </p>

          <div className="aion-fade-up aion-delay-3 flex flex-col sm:flex-row gap-4 items-center mt-11">
            <BtnPrimary onClick={onLogin}>Solicitar Demo <ChevronRight className="w-4 h-4 inline -mt-0.5"/></BtnPrimary>
            <BtnSecondary href="mailto:finnapps.externo@finnegans.com.ar">Contactar Ventas</BtnSecondary>
          </div>

          <div className="aion-fade-up aion-delay-4 flex flex-wrap justify-center gap-6 mt-9" style={{ fontSize:'.82rem', color:C.whiteDim }}>
            {['Modelado automático','Sin configuración compleja','Insights con IA'].map(t=>(
              <span key={t} className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color:C.cyan }}/>{t}</span>
            ))}
          </div>

          <div className="aion-fade-up aion-delay-4 flex flex-wrap justify-center gap-16 mt-16">
            {benefits.map((b,i)=>(
              <div key={i} className="text-center">
                <div className="aion-display font-bold" style={{ fontSize:'1.7rem', color:C.white, letterSpacing:'-0.02em' }}>
                  <span style={{ color:C.cyan }}>{b.metric}</span>
                </div>
                <div style={{ fontSize:'.76rem', color:C.whiteLo, textTransform:'uppercase', letterSpacing:'.06em', marginTop:4 }}>{b.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ padding:'120px 5%' }}>
          <div className="mx-auto grid md:grid-cols-2 gap-20 items-center" style={{ maxWidth:1100 }}>

            <div>
              <SectionLabel>Cómo funciona</SectionLabel>
              <SectionTitle>Tres pasos para<br/><span style={gradText}>inteligencia real</span></SectionTitle>

              <div className="flex flex-col gap-2 mt-12">
                {[
                  { n:'01', t:'Conectá tus fuentes', d:'BigQuery, Snowflake, Databricks, Amazon Athena, Google Cloud, AlloyDB o cualquier base de datos.' },
                  { n:'02', t:'IA modela automáticamente', d:'Detecta claves, relaciones, cardinalidades, jerarquías y dimensiones temporales sin intervención manual.' },
                  { n:'03', t:'Insights accionables', d:'Explicaciones automáticas, predicciones con ML y alertas inteligentes que anticipan problemas.' },
                ].map((s,i)=>(
                  <div key={i} className="aion-card-hover flex gap-5 items-start"
                       style={{ padding:'20px 22px', borderRadius:14, border:`1px solid transparent`, background:'transparent', cursor:'default' }}>
                    <div className="flex-shrink-0 flex items-center justify-center aion-display font-bold"
                         style={{ width:38, height:38, borderRadius:10, background:'linear-gradient(135deg,rgba(79,195,247,.15),rgba(79,195,247,.05))', border:`1px solid ${C.borderHi}`, fontSize:'.84rem', color:C.cyan }}>
                      {s.n}
                    </div>
                    <div>
                      <h3 style={{ fontSize:'.95rem', fontWeight:600, color:C.white, marginBottom:3 }}>{s.t}</h3>
                      <p style={{ fontSize:'.82rem', color:C.whiteDim, lineHeight:1.55 }}>{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative flex items-center justify-center" style={{ height:440 }}>

              <div className="aion-float absolute" style={{ top:'8%', left:'0%', zIndex:1, background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 17px', boxShadow:'0 4px 24px rgba(0,0,0,.25)', whiteSpace:'nowrap' }}>
                <div style={{ fontSize:'.67rem', color:C.whiteLo, textTransform:'uppercase', letterSpacing:'.06em' }}>Churn Rate</div>
                <div className="aion-display font-bold" style={{ fontSize:'1rem', color:C.white, marginTop:2 }}>3.2% <span style={{ color:'#4ade80' }}>↓ 0.8%</span></div>
              </div>
              <div className="aion-float aion-float-1 absolute" style={{ bottom:'8%', right:'-2%', zIndex:1, background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 17px', boxShadow:'0 4px 24px rgba(0,0,0,.25)', whiteSpace:'nowrap' }}>
                <div style={{ fontSize:'.67rem', color:C.whiteLo, textTransform:'uppercase', letterSpacing:'.06em' }}>Revenue MoM</div>
                <div className="aion-display font-bold" style={{ fontSize:'1rem', color:C.white, marginTop:2 }}>$124K <span style={{ color:'#4ade80' }}>↑ 12%</span></div>
              </div>

              <div style={{ width:320, background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:18, padding:28, boxShadow:'0 8px 48px rgba(0,0,0,.3)', position:'relative', zIndex:2 }}>
                <div className="flex justify-between items-center mb-6">
                  <h4 style={{ fontSize:'.88rem', fontWeight:600, color:C.white }}>Dashboard · Ventas</h4>
                  <div className="flex gap-1.5">
                    <span style={{ width:10, height:10, borderRadius:'50%', background:'#ef4444', display:'inline-block' }}/>
                    <span style={{ width:10, height:10, borderRadius:'50%', background:'#eab308', display:'inline-block' }}/>
                    <span style={{ width:10, height:10, borderRadius:'50%', background:'#22c55e', display:'inline-block' }}/>
                  </div>
                </div>

                <div className="flex items-end gap-2.5" style={{ height:100, marginBottom:10 }}>
                  {[55,78,45,90,62,83,50].map((h,i)=>(
                    <div key={i} className={`aion-bar aion-bar-d${i} flex-1`}
                         style={{
                           height:`${h}%`,
                           borderRadius:'6px 6px 0 0',
                           background: i===3 ? `linear-gradient(180deg,${C.accent},${C.cyan})` : `linear-gradient(180deg,${C.cyan},${C.cyanDim})`,
                         }}/>
                  ))}
                </div>
                <div className="flex gap-2.5 mb-5">
                  {['L','M','X','J','V','S','D'].map(d=>(
                    <span key={d} className="flex-1 text-center" style={{ fontSize:'.67rem', color:C.whiteLo }}>{d}</span>
                  ))}
                </div>

                <div className="flex gap-3">
                  {[{ v:'$48.2K', l:'Ingresos', t:'↑ 14.2%' },{ v:'1,240', l:'Conversiones', t:'↑ 8.1%' }].map((k,i)=>(
                    <div key={i} className="flex-1" style={{ background:'rgba(79,195,247,.06)', border:`1px solid ${C.border}`, borderRadius:10, padding:'11px 13px' }}>
                      <div className="aion-display font-bold" style={{ fontSize:'1.05rem', color:C.white }}>{k.v}</div>
                      <div style={{ fontSize:'.68rem', color:C.whiteLo, marginTop:2 }}>{k.l}</div>
                      <div style={{ fontSize:'.7rem', color:'#4ade80', marginTop:3 }}>{k.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ FEATURES ═══ */}
        <section style={{ padding:'100px 5% 80px' }}>
          <div className="mx-auto" style={{ maxWidth:1100 }}>
            <div className="text-center mb-14">
              <SectionLabel>Características</SectionLabel>
              <SectionTitle centered>Tecnología de <span style={gradText}>Vanguardia</span></SectionTitle>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((f,i)=>(
                <div key={i} className="aion-card-hover"
                     style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:16, padding:'28px 24px', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:-36, right:-36, width:110, height:110, borderRadius:'50%', background:'radial-gradient(circle,rgba(79,195,247,.08),transparent 70%)', pointerEvents:'none' }}/>
                  <div className="flex items-center justify-center mb-5"
                       style={{ width:44, height:44, borderRadius:12, background:'linear-gradient(135deg,rgba(79,195,247,.12),rgba(79,195,247,.04))', border:`1px solid ${C.border}`, color:C.cyan }}>
                    {f.icon}
                  </div>
                  <h3 style={{ fontSize:'.94rem', fontWeight:600, color:C.white, marginBottom:8 }}>{f.title}</h3>
                  <p style={{ fontSize:'.81rem', color:C.whiteDim, lineHeight:1.55 }}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        
        {/* ═══ CTA ═══ */}
        <section style={{ padding:'100px 5% 120px' }}>
          <div className="mx-auto" style={{ maxWidth:860 }}>
            <div className="text-center relative overflow-hidden"
                 style={{ background:C.bgCard, border:`1px solid ${C.borderHi}`, borderRadius:28, padding:'72px 52px' }}>
              <div style={{ position:'absolute', top:-60, left:'50%', transform:'translateX(-50%)', width:500, height:500, background:'radial-gradient(ellipse,rgba(79,195,247,.08) 0%,transparent 65%)', pointerEvents:'none' }}/>

              <div style={{ position:'relative', zIndex:1 }}>
                <SectionLabel>Empezá hoy</SectionLabel>
                <h2 className="aion-display font-bold text-3xl md:text-4xl leading-tight text-center" style={{ letterSpacing:'-0.03em', color:C.white }}>
                  ¿Listo para trabajar con un BI<br/>que <span style={gradText}>piensa con vos</span>?
                </h2>
                <p style={{ color:C.whiteDim, fontSize:'.95rem', lineHeight:1.65, maxWidth:500, margin:'16px auto 34px' }}>
                  Solicitá una demo personalizada y descubrí cómo AION BI puede transformar tus datos en decisiones estratégicas.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <BtnPrimary href="https://calendly.com/finnapps-externo-finnegans/demo-web">Agendar Demo</BtnPrimary>
                  <BtnSecondary href="mailto:finnapps.externo@finnegans.com.ar">Contactar Ventas</BtnSecondary>
                </div>

                <div className="flex flex-wrap justify-center gap-7 mt-8">
                  {['Demo personalizada','Sin compromisos','Soporte incluido'].map(t=>(
                    <span key={t} className="flex items-center gap-1.5" style={{ fontSize:'.75rem', color:C.whiteLo }}>
                      <span style={{ color:'#4ade80' }}>✓</span>{t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer style={{ borderTop:`1px solid ${C.border}`, padding:'56px 5% 36px' }}>
          <div className="mx-auto grid grid-cols-2 md:grid-cols-4 gap-10" style={{ maxWidth:1100 }}>
            <div>
              <div className="flex items-center mb-3.5">
                <LogoMark size={100}/>
              </div>
              <p style={{ fontSize:'.79rem', color:C.whiteLo, lineHeight:1.6, maxWidth:240 }}>
                El BI que piensa con vos.
              </p>
            </div>
            {[
              { title:'Producto',   links:['Características','Cómo Funciona','Casos de Uso'] },
              { title:'Contacto',   links:['Email','Agendar Demo'] },
            ].map((col,i)=>(
              <div key={i}>
                <h5 style={{ fontSize:'.72rem', textTransform:'uppercase', letterSpacing:'.1em', color:C.whiteLo, marginBottom:14 }}>{col.title}</h5>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map(l=>(
                    <li key={l}>
                      <a href="#" style={{ fontSize:'.82rem', color:C.whiteDim, transition:'color .2s' }}
                         onMouseEnter={e=>e.currentTarget.style.color=C.cyan}
                         onMouseLeave={e=>e.currentTarget.style.color=C.whiteDim}>{l}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mx-auto flex flex-col sm:flex-row justify-between items-center mt-10 pt-6" style={{ maxWidth:1100, borderTop:`1px solid ${C.border}` }}>
            <span style={{ fontSize:'.75rem', color:C.whiteLo }}>© 2026 AION BI. Todos los derechos reservados.</span>
          </div>
        </footer>

      </div>
    </div>
  );
}